import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Play } from 'lucide-react';

import { Button } from 'components/ui/button';
import { Badge } from 'components/ui/badge';
import { Label } from 'components/ui/label';
import { Textarea } from 'components/ui/textarea';
import { useCreateApiContext } from '../create-api-context';
import {
  useCreateApiAction,
  useGetToolById,
  useGetToolConnection,
  useTestApiAction,
} from 'features/chat/agent-chat-module/hooks/use-gpt-chat';
import type { ITestApiActionResponse } from 'features/chat/agent-chat-module/services/gpt-chat.service';
import { useToast } from 'hooks/use-toast';
import { useConfigStore } from 'state/store/config/useConfigStore';
import {
  AUTO_AUTH_NOT_CONNECTED_MESSAGE,
  isAutoAuthTool,
  normalizeToolDetails,
} from '../../components/tool-detail-utils';
import { getGraphQLOperationType } from './utils/graphql-utils';

/** Pulls a GraphQL `errors[]` array out of a debug response (top-level or under `data`/`result`). */
const extractGraphQLErrors = (raw: string): string[] => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const candidates = [parsed, parsed.data, parsed.result].filter(
      (value): value is Record<string, unknown> => !!value && typeof value === 'object'
    );
    for (const candidate of candidates) {
      const errors = candidate.errors;
      if (Array.isArray(errors) && errors.length > 0) {
        return errors.map((error) => {
          if (typeof error === 'string') return error;
          const message = (error as { message?: unknown })?.message;
          return typeof message === 'string' ? message : JSON.stringify(error);
        });
      }
    }
  } catch {
    /* non-JSON response — nothing to surface */
  }
  return [];
};

const buildDebugPayload = (schema: Record<string, unknown>) => {
  const buildValue = (definition: unknown): unknown => {
    if (!definition || typeof definition !== 'object' || Array.isArray(definition)) return '';

    const record = definition as Record<string, unknown>;
    if (record.DefaultValue !== undefined && record.DefaultValue !== null && record.DefaultValue !== '') {
      return record.DefaultValue;
    }

    const type = String(record.Type || '').toLowerCase();

    if (type === 'object') {
      const nested: Record<string, unknown> = {};
      Object.entries(record).forEach(([key, value]) => {
        if (['Name', 'Description', 'Type', 'Required', 'DefaultValue', 'Location'].includes(key)) return;
        nested[key] = buildValue(value);
      });
      return nested;
    }

    if (type === 'array') return [];
    if (type === 'number' || type === 'integer') return 0;
    if (type === 'boolean') return false;
    return '';
  };

  return Object.fromEntries(Object.entries(schema).map(([key, value]) => [key, buildValue(value)]));
};

const getTestApiErrorMessage = (
  response: ITestApiActionResponse,
  fallback: string,
) => response.error || response.detail || response.message || fallback;

export function CreateApiDebuggingForm({ toolId }: { toolId: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isBlocksTool = searchParams.get('isBlocksTool') === 'true';
  const queryClient = useQueryClient();
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const projectKey = configurationSettings?.blocksKey || '';
  const { formData, resetFormData } = useCreateApiContext();
  const { mutateAsync: createApiAction, isPending } = useCreateApiAction();
  const { mutateAsync: testApiAction, isPending: isTesting } = useTestApiAction();
  const toolQuery = useGetToolById({ toolId, projectKey }, { enabled: !!toolId && !!projectKey });
  const tool = useMemo(() => normalizeToolDetails(toolQuery.data), [toolQuery.data]);
  const requiresAutoAuth = isAutoAuthTool(tool);
  const { data: connection } = useGetToolConnection(toolId, {
    enabled: requiresAutoAuth && !!toolId,
  });
  const { toast } = useToast();

  const [debugText, setDebugText] = useState('{}');
  const [debugResult, setDebugResult] = useState('');
  const [debugStatus, setDebugStatus] = useState<'failed' | 'success' | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [activeDebugType, setActiveDebugType] = useState<'run' | 'schema' | null>(null);
  const graphqlErrors = useMemo(() => extractGraphQLErrors(debugResult), [debugResult]);

  useEffect(() => {
    if (formData.input_parameters) {
      setDebugText(JSON.stringify(buildDebugPayload(formData.input_parameters), null, 2));
    }
  }, [formData.input_parameters]);

  const getAutoAuthConnectionError = () => {
    if (!requiresAutoAuth || connection?.connected) {
      return null;
    }

    return (
      t('AUTO_AUTH_NOT_CONNECTED', {
        defaultValue: AUTO_AUTH_NOT_CONNECTED_MESSAGE,
      }) || AUTO_AUTH_NOT_CONNECTED_MESSAGE
    );
  };

  const showAutoAuthConnectionWarning = (message: string) => {
    setDebugStatus('failed');
    setErrorMessage(message);
    toast({
      variant: 'destructive',
      title: t('CONNECTION_REQUIRED') || 'Connection required',
      description: message,
    });
  };

  const handleRunDebug = async (extractionOnly = false) => {
    if (!formData.actionId) {
      toast({
        variant: 'destructive',
        title: t('ERROR') || 'Error',
        description:
          t('ACTION_ID_REQUIRED_FOR_DEBUG') ||
          'Action ID is required to run debug. Please save the action first.',
      });
      return;
    }

    const autoAuthConnectionError = getAutoAuthConnectionError();
    if (autoAuthConnectionError) {
      showAutoAuthConnectionWarning(autoAuthConnectionError);
      return;
    }

    const debugType = extractionOnly ? 'schema' : 'run';
    setActiveDebugType(debugType);
    setDebugStatus(null);
    setErrorMessage('');

    try {
      const parameters = JSON.parse(debugText || '{}');

      const response = await testApiAction({
        toolId,
        actionId: formData.actionId,
        payload: {
          parameters,
          test_config: {},
          project_key: projectKey,
          extraction_only: extractionOnly,
          is_open_tool: isBlocksTool,
        },
      });

      const responseData = response as unknown as Record<string, unknown>;
      const resultData = responseData.result || responseData.response_details || response;
      const isSuccess =
        responseData.success !== undefined ? Boolean(responseData.success) : Boolean(response.is_success);

      if (isSuccess) {
        setDebugStatus('success');
        setDebugResult(JSON.stringify(resultData, null, 2));
        toast({
          variant: 'success',
          title: t('DEBUG_COMPLETE') || 'Debug complete',
          description: extractionOnly
            ? t('SCHEMA_DEBUG_COMPLETED') || 'Schema debug completed successfully.'
            : t('DEBUG_TEST_COMPLETED') || 'Debug test completed successfully.',
        });
      } else {
        const failureMessage = getTestApiErrorMessage(
          response,
          t('TEST_FAILED') || 'Test failed',
        );
        setDebugStatus('failed');
        setErrorMessage(failureMessage);
        setDebugResult(JSON.stringify(resultData, null, 2));
        toast({
          variant: 'destructive',
          title: t('DEBUG_FAILED') || 'Debug failed',
          description: failureMessage,
        });
      }
    } catch (error) {
      setDebugStatus('failed');
      const errorMsg =
        error instanceof Error ? error.message : t('UNKNOWN_ERROR') || 'Unknown error occurred';
      setErrorMessage(errorMsg);
      setDebugResult(JSON.stringify({ error: errorMsg }, null, 2));
      toast({
        variant: 'destructive',
        title: t('DEBUG_FAILED') || 'Debug failed',
        description: errorMsg,
      });
    } finally {
      setActiveDebugType(null);
    }
  };

  const handleComplete = async () => {
    const autoAuthConnectionError = getAutoAuthConnectionError();
    if (autoAuthConnectionError) {
      showAutoAuthConnectionWarning(autoAuthConnectionError);
      return;
    }

    try {
      const isGraphQL = (formData.method || '').toUpperCase() === 'GRAPHQL';
      const payload = {
        name: formData.name,
        description: formData.description,
        endpoint: formData.endpoint,
        method: formData.method,
        input_parameters: formData.input_parameters,
        output_parameters: formData.output_parameters,
        is_open_tool: isBlocksTool,
        ...(isGraphQL
          ? {
              graphql_query: formData.graphql_query || '',
              graphql_operation_type:
                formData.graphql_operation_type ||
                getGraphQLOperationType(formData.graphql_query || ''),
            }
          : {}),
      };

      const response = await createApiAction({
        toolId,
        actionId: formData.actionId,
        projectKey,
        payload,
      });

      if (response.is_success !== false) {
        toast({
          variant: 'success',
          title: t('SUCCESS') || 'Success',
          description: formData.actionId
            ? t('API_ACTION_UPDATED_SUCCESSFULLY') || 'API action updated successfully'
            : t('API_ACTION_CREATED_SUCCESSFULLY') || 'API action created successfully',
        });
        queryClient.invalidateQueries({ queryKey: ['tools', 'detail', toolId] });
        resetFormData();
        navigate(`/tool-details/${encodeURIComponent(toolId)}?tab=api`);
      } else {
        throw new Error(
          response.message || response.detail || t('FAILED_TO_SAVE_API_ACTION') || 'Failed to save API action'
        );
      }
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('ERROR') || 'Error',
        description:
          error instanceof Error
            ? error.message
            : t('FAILED_TO_SAVE_API_ACTION') || 'An error occurred while saving the API action',
      });
    }
  };

  return (
    <div className="w-full min-w-0">
      <div className="mb-6">
        <h2 className="text-2xl font-semibold">{t('DEBUGGING') || 'Debugging'}</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('CREATE_API_DEBUGGING_DESCRIPTION') ||
            'Test your API with sample requests to validate inputs, outputs, and logic.'}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 w-full">
        <div className="flex flex-col rounded-lg border bg-background" style={{ height: '550px' }}>
          <div className="border-b p-4">
            <h3 className="font-medium">{t('INPUT_PARAMETERS') || 'Input parameters'}</h3>
          </div>

          <div className="flex flex-1 flex-col overflow-hidden p-4">
            <div className="flex-1 space-y-3">
              <Label htmlFor="debug-request">
                {t('DEBUG_REQUEST_JSON') || 'Debug Request JSON'}
              </Label>
              <Textarea
                id="debug-request"
                value={debugText}
                onChange={(event) => setDebugText(event.target.value)}
                rows={18}
                className="font-mono text-sm"
              />
            </div>
            <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:justify-end">
              <Button
                size="sm"
                variant="outline"
                className="h-10 w-full sm:h-9 sm:w-auto"
                onClick={() => handleRunDebug(false)}
                disabled={isTesting}
              >
                <Play className="mr-2 inline h-4 w-4" />
                {isTesting && activeDebugType === 'run'
                  ? t('RUNNING') || 'Running...'
                  : t('RUN_DEBUG') || 'Run Debug'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-10 w-full sm:h-9 sm:w-auto"
                onClick={() => handleRunDebug(true)}
                disabled={isTesting}
              >
                <Play className="mr-2 inline h-4 w-4" />
                {isTesting && activeDebugType === 'schema'
                  ? t('RUNNING') || 'Running...'
                  : t('DEBUG_SCHEMA') || 'Debug Schema'}
              </Button>
            </div>
          </div>
        </div>

        <div className="flex flex-col rounded-lg border bg-background" style={{ height: '550px' }}>
          <div className="border-b p-4">
            <div className="flex items-center justify-between">
              <h3 className="font-medium">{t('DEBUG_RESULT') || 'Debug result'}</h3>
              {debugStatus ? (
                <Badge variant={debugStatus === 'failed' ? 'destructive' : 'default'} className="rounded-sm">
                  {debugStatus === 'failed'
                    ? t('FAILED') || 'Failed'
                    : t('SUCCESS') || 'Success'}
                </Badge>
              ) : null}
            </div>
          </div>

          <div className="flex flex-1 flex-col overflow-hidden p-4">
            <div className="flex-1 space-y-3">
              <Label htmlFor="debug-response">{t('RESPONSE') || 'Response'}</Label>
              <Textarea
                id="debug-response"
                value={debugResult}
                rows={18}
                className="font-mono text-sm"
                readOnly
                placeholder={
                  t('RESPONSE_PAYLOAD_PLACEHOLDER') || 'Response payload will appear here.'
                }
              />
              {graphqlErrors.length > 0 ? (
                <div className="rounded-md bg-destructive/10 p-2">
                  <p className="mb-1 text-xs font-medium text-destructive">
                    {t('GRAPHQL_ERRORS') || 'GraphQL errors'}
                  </p>
                  <ul className="list-disc space-y-1 pl-4">
                    {graphqlErrors.map((message, index) => (
                      <li key={index} className="text-xs text-destructive">
                        {message}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {debugStatus === 'failed' && errorMessage ? (
                <div className="rounded-md bg-destructive/10 p-2">
                  <p className="text-xs text-destructive">{errorMessage}</p>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 flex items-center justify-start">
        <Button onClick={handleComplete} disabled={isPending} className="px-8">
          {isPending ? t('CREATING') || 'Creating...' : t('COMPLETE') || 'Complete'}
        </Button>
      </div>
    </div>
  );
}
